import React, { useState, useEffect, useRef, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Swal from "sweetalert2";
import axios from "axios";
import { baseurl } from "../../../Basurl/Baseurl";
import {
  createPhysiotherapyReferral,
  updatePhysiotherapyCase,
  fetchPhysiotherapyDetails,
  PHYSIO_CATEGORIES,
  PHYSIO_PRIORITIES,
  PHYSIO_STATUSES,
  PHYSIO_MODALITIES,
} from "../../../services/physiotherapyApi";

const EMPTY_FORM = {
  referralReason: "",
  clinicalDiagnosis: "",
  category: "Orthopedic",
  affectedBodyPart: "",
  priority: "Normal",
  prescribedSessions: 10,
  sessionFrequency: "3 times a week",
  treatmentGoals: "",
  specialInstructions: "",
  therapistName: "",
  therapistPhone: "",
  therapistRole: "Senior Physiotherapist",
  mobilityScore: "Independent",
  baselinePainScore: 5,
  rangeOfMotionNotes: "",
  postureEvaluation: "",
  assessmentNotes: "",
  startDate: new Date().toISOString().split("T")[0],
  expectedEndDate: "",
  notes: "",
  status: "Referred",
};

const parseInitialAssessment = (caseObj = {}) => {
  let ia = caseObj.initialAssessment ?? caseObj.initial_assessment ?? {};
  if (typeof ia === "string") {
    try {
      ia = JSON.parse(ia);
    } catch {
      ia = {};
    }
  }
  if (!ia || typeof ia !== "object") ia = {};

  return {
    mobilityScore:
      ia.mobilityScore || caseObj.mobilityScore || "Independent",
    baselinePainScore:
      ia.baselinePainScore ?? caseObj.baselinePainScore ?? 5,
    rangeOfMotionNotes:
      ia.rangeOfMotionNotes ||
      ia.romNotes ||
      caseObj.rangeOfMotionNotes ||
      "",
    postureEvaluation:
      ia.postureEvaluation || caseObj.postureEvaluation || "",
    assessmentNotes:
      ia.notes ||
      ia.assessmentNotes ||
      caseObj.assessmentNotes ||
      "",
  };
};

const parseTherapist = (caseObj = {}) => {
  const raw = caseObj.assignedPhysiotherapist ?? caseObj.therapist ?? {};
  if (typeof raw === "string") {
    return { name: raw, phone: "", role: "Senior Physiotherapist" };
  }
  let phone = String(raw?.phone || raw?.mobile || "").trim();
  // Form input stores local number; dial code is prepended on submit
  phone = phone.replace(/^\+91\s*/i, "").trim();
  return {
    name: raw?.name || raw?.therapistName || "",
    phone,
    role: raw?.role || "Senior Physiotherapist",
  };
};

const normalizeModalityLabel = (value) => {
  const label = String(value || "").trim();
  if (!label) return "";

  const lower = label.toLowerCase();
  const exact = PHYSIO_MODALITIES.find((opt) => opt.toLowerCase() === lower);
  if (exact) return exact;

  // API often stores short names: "TENS", "Cryotherapy", "Ultrasound"
  const fuzzy = PHYSIO_MODALITIES.find((opt) => {
    const optLower = opt.toLowerCase();
    const optCore = optLower.split("(")[0].split("/")[0].trim();
    const labelCore = lower.split("(")[0].split("/")[0].trim();
    return (
      optLower.includes(labelCore) ||
      labelCore.includes(optCore) ||
      optCore.startsWith(labelCore) ||
      labelCore.startsWith(optCore)
    );
  });
  return fuzzy || label;
};

const coerceModalitiesList = (raw) => {
  if (!raw && raw !== 0) return [];

  if (Array.isArray(raw)) {
    return raw.flatMap((item) => {
      if (typeof item === "string") return [item];
      if (item && typeof item === "object") {
        return [
          item.name ||
            item.label ||
            item.modality ||
            item.modalityName ||
            item.title ||
            item.value ||
            "",
        ];
      }
      return [];
    });
  }

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed) || typeof parsed === "object") {
        return coerceModalitiesList(parsed);
      }
    } catch {
      // comma / pipe separated plain string
    }
    return trimmed.split(/[,|;]+/).map((s) => s.trim()).filter(Boolean);
  }

  if (typeof raw === "object") {
    // { "TENS": true, "Cryotherapy": true } or similar map
    return Object.keys(raw).filter((key) => {
      const val = raw[key];
      return val === true || val === 1 || val === "1" || val === "true" || val === "yes";
    });
  }

  return [];
};

const collectRawModalities = (caseObj = {}) => {
  const ia =
    (caseObj.initialAssessment && typeof caseObj.initialAssessment === "object"
      ? caseObj.initialAssessment
      : null) ||
    (caseObj.initial_assessment && typeof caseObj.initial_assessment === "object"
      ? caseObj.initial_assessment
      : null) ||
    {};

  const candidates = [
    caseObj.modalitiesUsed,
    caseObj.modalities,
    caseObj.therapyModalities,
    caseObj.prescribedModalities,
    caseObj.electrotherapy,
    caseObj.electrotherapyModalities,
    caseObj.selectedModalities,
    ia.modalitiesUsed,
    ia.modalities,
    ia.therapyModalities,
  ];

  // Also collect from logged sessions (older cases may only store modalities there)
  const sessions = Array.isArray(caseObj.sessions)
    ? caseObj.sessions
    : Array.isArray(caseObj.sessionHistory)
    ? caseObj.sessionHistory
    : Array.isArray(caseObj.physiotherapySessions)
    ? caseObj.physiotherapySessions
    : [];

  sessions.forEach((sess) => {
    if (!sess || typeof sess !== "object") return;
    candidates.push(sess.modalitiesUsed, sess.modalities, sess.therapyModalities);
  });

  // Union all non-empty sources (don't stop at first key — it may be an empty array)
  const combined = [];
  candidates.forEach((candidate) => {
    coerceModalitiesList(candidate).forEach((item) => {
      if (item) combined.push(item);
    });
  });

  // Last resort: scan nested strings/arrays for known modality labels (exact / full-label contains)
  if (combined.length === 0) {
    const found = new Set();
    const tryAdd = (text) => {
      const value = String(text || "").trim();
      if (!value) return;
      const lower = value.toLowerCase();
      PHYSIO_MODALITIES.forEach((mod) => {
        const modLower = mod.toLowerCase();
        if (lower === modLower || lower.includes(modLower) || modLower.includes(lower)) {
          // Avoid tiny fragments (e.g. "Heat") falsely matching
          if (lower.length >= 8 || lower === modLower) {
            found.add(mod);
          }
        }
      });
    };
    const visit = (node, depth = 0) => {
      if (!node || depth > 4) return;
      if (typeof node === "string") {
        tryAdd(node);
        coerceModalitiesList(node).forEach(tryAdd);
        return;
      }
      if (Array.isArray(node)) {
        node.forEach((item) => visit(item, depth + 1));
        return;
      }
      if (typeof node === "object") {
        Object.values(node).forEach((val) => visit(val, depth + 1));
      }
    };
    visit(caseObj);
    return Array.from(found);
  }

  return combined;
};

const MODALITY_CACHE_KEY = "omca_physio_case_modalities_v1";

const cacheCaseModalities = (caseId, modalities) => {
  if (!caseId) return;
  try {
    const all = JSON.parse(localStorage.getItem(MODALITY_CACHE_KEY) || "{}");
    all[String(caseId)] = Array.isArray(modalities) ? modalities : [];
    localStorage.setItem(MODALITY_CACHE_KEY, JSON.stringify(all));
  } catch {
    // ignore quota / private mode
  }
};

const readCachedCaseModalities = (caseId) => {
  if (!caseId) return [];
  try {
    const all = JSON.parse(localStorage.getItem(MODALITY_CACHE_KEY) || "{}");
    const raw = all[String(caseId)];
    return Array.isArray(raw) ? raw.map(normalizeModalityLabel).filter(Boolean) : [];
  } catch {
    return [];
  }
};

const parseModalities = (caseObj = {}) => {
  const seen = new Set();
  const result = [];
  collectRawModalities(caseObj).forEach((item) => {
    const normalized = normalizeModalityLabel(item);
    if (!normalized) return;
    const key = normalized.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    result.push(normalized);
  });

  // Fallback: browser cache if API omitted modalitiesUsed on GET
  if (result.length === 0) {
    const cached = readCachedCaseModalities(caseObj._id || caseObj.id);
    cached.forEach((normalized) => {
      const key = normalized.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      result.push(normalized);
    });
  }

  return result;
};

const isModalitySelected = (mod, selectedList = []) => {
  const target = normalizeModalityLabel(mod).toLowerCase();
  return selectedList.some(
    (s) => normalizeModalityLabel(s).toLowerCase() === target
  );
};

/** Details API sometimes wraps the case one or more levels deeper */
const unwrapCasePayload = (payload, depth = 0) => {
  if (!payload || typeof payload !== "object" || depth > 3) return null;

  const looksLikeCase =
    payload.clinicalDiagnosis != null ||
    payload.modalitiesUsed != null ||
    payload.modalities != null ||
    payload.initialAssessment != null ||
    payload.initial_assessment != null ||
    payload.referralReason != null ||
    payload.patientId != null ||
    payload.prescribedSessions != null ||
    payload.assignedPhysiotherapist != null ||
    Array.isArray(payload.sessions);

  if (looksLikeCase) return payload;

  const nested =
    payload.data ||
    payload.details ||
    payload.case ||
    payload.physiotherapy ||
    payload.physioCase ||
    payload.result ||
    null;

  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return unwrapCasePayload(nested, depth + 1);
  }

  // Array of one case
  if (Array.isArray(nested) && nested[0] && typeof nested[0] === "object") {
    return unwrapCasePayload(nested[0], depth + 1);
  }

  return payload;
};

const mapCaseToForm = (caseObj = {}) => {
  const assessment = parseInitialAssessment(caseObj);
  const therapist = parseTherapist(caseObj);
  const patientObj =
    caseObj.patientObjectId && typeof caseObj.patientObjectId === "object"
      ? caseObj.patientObjectId
      : {};

  return {
    patient: {
      _id: patientObj._id || caseObj.patientObjectId || caseObj.patient_id,
      patientId:
        caseObj.patientId ||
        patientObj.patientId ||
        patientObj.patientNumber ||
        caseObj.patient_id,
      name:
        patientObj.patient_name ||
        caseObj.patientName ||
        caseObj.patient_name ||
        "Patient",
      phone: patientObj.phone || patientObj.mobile || "",
      country: patientObj.country || "",
    },
    formData: {
      referralReason: caseObj.referralReason || "",
      clinicalDiagnosis: caseObj.clinicalDiagnosis || "",
      category: caseObj.category || "Orthopedic",
      affectedBodyPart: caseObj.affectedBodyPart || "",
      priority: caseObj.priority || "Normal",
      prescribedSessions: caseObj.prescribedSessions || 10,
      sessionFrequency: caseObj.sessionFrequency || "3 times a week",
      treatmentGoals: caseObj.treatmentGoals || "",
      specialInstructions: caseObj.specialInstructions || "",
      therapistName: therapist.name,
      therapistPhone: therapist.phone,
      therapistRole: therapist.role,
      mobilityScore: assessment.mobilityScore,
      baselinePainScore: assessment.baselinePainScore,
      rangeOfMotionNotes: assessment.rangeOfMotionNotes,
      postureEvaluation: assessment.postureEvaluation,
      assessmentNotes: assessment.assessmentNotes,
      startDate: caseObj.startDate
        ? String(caseObj.startDate).split("T")[0]
        : new Date().toISOString().split("T")[0],
      expectedEndDate: caseObj.expectedEndDate
        ? String(caseObj.expectedEndDate).split("T")[0]
        : "",
      notes: caseObj.notes || "",
      status: caseObj.status || "In Progress",
    },
    modalities: parseModalities(caseObj),
  };
};

export default function ReferralModal({
  open,
  onClose,
  onSuccess,
  prefilledPatient = null,
  editCase = null,
}) {
  const [patientsList, setPatientsList] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(prefilledPatient);
  const [patientDropdownOpen, setPatientDropdownOpen] = useState(false);
  const [patientSearchTerm, setPatientSearchTerm] = useState("");
  const patientContainerRef = useRef(null);
  const patientInputRef = useRef(null);

  const [formData, setFormData] = useState({ ...EMPTY_FORM });

  const [selectedModalities, setSelectedModalities] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingEditCase, setLoadingEditCase] = useState(false);
  const contentRef = React.useRef(null);
  const [therapistDialCode] = useState("+91");

  // Filter patients by search term
  const filteredPatients = useMemo(() => {
    if (!patientSearchTerm.trim()) return patientsList;
    const term = patientSearchTerm.toLowerCase().trim();
    return patientsList.filter((p) => {
      const name = String(p.name || p.patient_name || "").toLowerCase();
      const pId = String(p.patientId || p.patient_id || "").toLowerCase();
      const phone = String(p.phone || p.contact_number || "").replace(/[^0-9]/g, "");
      const cleanTerm = term.replace(/[^0-9]/g, "");
      const country = String(p.country || "").toLowerCase();
      return (
        name.includes(term) ||
        pId.includes(term) ||
        (cleanTerm && phone.includes(cleanTerm)) ||
        country.includes(term)
      );
    });
  }, [patientsList, patientSearchTerm]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (patientContainerRef.current && !patientContainerRef.current.contains(e.target)) {
        setPatientDropdownOpen(false);
        setPatientSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (open) {
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    }
  }, [open]);

  // Prefill / reset form when modal opens. List rows are sparse — fetch full details for edit.
  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const applyCase = (caseObj, { mergeModalities = false } = {}) => {
      if (cancelled || !caseObj) return;
      const mapped = mapCaseToForm(unwrapCasePayload(caseObj) || caseObj);
      setSelectedPatient(mapped.patient);
      setFormData(mapped.formData);
      setSelectedModalities((prev) => {
        if (!mergeModalities) return mapped.modalities;
        // Keep previously parsed modalities if the newest payload omitted them
        if (mapped.modalities.length > 0) return mapped.modalities;
        return prev;
      });
    };

    if (editCase) {
      // Instant fill from whatever the parent passed (list/detail)
      applyCase(editCase);

      const caseId = editCase._id || editCase.id;
      if (!caseId) return undefined;

      setLoadingEditCase(true);
      fetchPhysiotherapyDetails(caseId)
        .then((data) => {
          if (cancelled) return;
          const detail = unwrapCasePayload(data?.data || data);
          if (detail && typeof detail === "object") {
            applyCase(detail, { mergeModalities: true });
          }
        })
        .catch((err) => {
          console.error("Failed to load physiotherapy case for edit:", err);
        })
        .finally(() => {
          if (!cancelled) setLoadingEditCase(false);
        });
    } else {
      setFormData({
        ...EMPTY_FORM,
        startDate: new Date().toISOString().split("T")[0],
      });
      setSelectedModalities([]);
      setSelectedPatient(prefilledPatient || null);
      setLoadingEditCase(false);
    }

    return () => {
      cancelled = true;
    };
  }, [open, editCase, prefilledPatient]);

  // Load all patients when modal opens (create flow only)
  useEffect(() => {
    if (!open) return;
    if (editCase || prefilledPatient) return;

    const fetchAllPatients = async () => {
      setLoadingPatients(true);
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const firstRes = await axios.get(
          `${baseurl}all_patients?page=1&limit=3000&search=`,
          { headers, timeout: 20000 }
        );
        const firstData = firstRes.data;
        const allPatients = [
          ...(firstData?.details ||
            firstData?.data?.details ||
            firstData?.patients ||
            firstData?.data?.patients ||
            (Array.isArray(firstData?.data) ? firstData.data : []) ||
            (Array.isArray(firstData) ? firstData : []) ||
            []),
        ];

        // Handle pagination if backend caps limit
        const totalPages = Number(firstData?.pagination?.totalPages || 1);
        if (totalPages > 1) {
          const pagePromises = [];
          for (let p = 2; p <= totalPages; p++) {
            pagePromises.push(
              axios
                .get(`${baseurl}all_patients?page=${p}&limit=500&search=`, {
                  headers,
                  timeout: 15000,
                })
                .then((res) => {
                  const pData = res.data;
                  return (
                    pData?.details ||
                    pData?.data?.details ||
                    pData?.patients ||
                    pData?.data?.patients ||
                    (Array.isArray(pData?.data) ? pData.data : []) ||
                    []
                  );
                })
                .catch(() => [])
            );
          }
          const remainingPages = await Promise.all(pagePromises);
          remainingPages.forEach((pList) => allPatients.push(...pList));
        }

        setPatientsList(Array.isArray(allPatients) ? allPatients : []);
      } catch (err) {
        console.error("Patient list load error:", err);
      } finally {
        setLoadingPatients(false);
      }
    };

    fetchAllPatients();
  }, [open, editCase, prefilledPatient]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleToggleModality = (mod) => {
    const canonical = normalizeModalityLabel(mod);
    setSelectedModalities((prev) => {
      const exists = isModalitySelected(canonical, prev);
      if (exists) {
        return prev.filter(
          (m) =>
            normalizeModalityLabel(m).toLowerCase() !==
            normalizeModalityLabel(canonical).toLowerCase()
        );
      }
      return [...prev, canonical];
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const patientId =
      selectedPatient?.patientId ||
      selectedPatient?.patient_id ||
      selectedPatient?._id;

    if (!patientId) {
      return Swal.fire("Validation Error", "Please search and select a patient", "warning");
    }

    if (!formData.clinicalDiagnosis.trim()) {
      return Swal.fire(
        "Validation Error",
        "Please enter Clinical Diagnosis",
        "warning"
      );
    }

    setSubmitting(true);
    try {
      const fullPhone = formData.therapistPhone
        ? `${therapistDialCode ? therapistDialCode + " " : ""}${formData.therapistPhone}`.trim()
        : "";

      const assignedPhysiotherapistObj = {
        name: formData.therapistName || "",
        phone: fullPhone,
        role: formData.therapistRole || "Senior Physiotherapist",
      };

      const normalizedModalities = selectedModalities
        .map((m) => normalizeModalityLabel(m))
        .filter(Boolean);

      const initialAssessmentObj = {
        mobilityScore: formData.mobilityScore,
        baselinePainScore: Number(formData.baselinePainScore) || 0,
        rangeOfMotionNotes: formData.rangeOfMotionNotes || "",
        postureEvaluation: formData.postureEvaluation || "",
        notes: formData.assessmentNotes || "",
        modalitiesUsed: normalizedModalities,
      };

      const payload = {
        patientId,
        ...(selectedPatient?._id ? { patientObjectId: selectedPatient._id } : {}),
        clinicalDiagnosis: formData.clinicalDiagnosis.trim(),
        referralReason: formData.referralReason.trim(),
        category: formData.category,
        affectedBodyPart: formData.affectedBodyPart.trim(),
        priority: formData.priority,
        prescribedSessions: Number(formData.prescribedSessions) || 1,
        sessionFrequency: formData.sessionFrequency,
        treatmentGoals: formData.treatmentGoals,
        specialInstructions: formData.specialInstructions,
        modalitiesUsed: normalizedModalities,
        modalities: normalizedModalities,
        assignedPhysiotherapist: assignedPhysiotherapistObj,
        initialAssessment: initialAssessmentObj,
        startDate: formData.startDate,
        ...(formData.expectedEndDate ? { expectedEndDate: formData.expectedEndDate } : {}),
        status: formData.status || (editCase ? "In Progress" : "Referred"),
        notes: formData.notes,
      };

      if (editCase) {
        await updatePhysiotherapyCase(editCase._id, payload);
        cacheCaseModalities(editCase._id, normalizedModalities);
        Swal.fire({
          title: "Case Updated!",
          text: "Physiotherapy case details have been updated successfully.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        const created = await createPhysiotherapyReferral(payload);
        const createdId =
          created?.data?._id ||
          created?.data?.id ||
          created?._id ||
          created?.id ||
          null;
        if (createdId) cacheCaseModalities(createdId, normalizedModalities);
        Swal.fire({
          title: "Referral Created!",
          text: "Patient has been referred to Physiotherapy successfully.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      const msg =
        typeof error === "string"
          ? error
          : error?.message ||
            (editCase ? "Failed to update case" : "Failed to create referral");
      Swal.fire("Error", msg, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const getPainLevelBadge = (score) => {
    const num = Number(score);
    if (num === 0) return { label: "0 - No Pain", bg: "#10b981", text: "#fff" };
    if (num <= 3) return { label: `${num} - Mild Pain`, bg: "#22c55e", text: "#fff" };
    if (num <= 6) return { label: `${num} - Moderate Pain`, bg: "#f59e0b", text: "#fff" };
    if (num <= 8) return { label: `${num} - Severe Pain`, bg: "#f97316", text: "#fff" };
    return { label: `${num} - Worst Possible Pain`, bg: "#ef4444", text: "#fff" };
  };

  const painBadge = getPainLevelBadge(formData.baselinePainScore);

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      className="physio-dialog"
      scroll="paper"
      disableAutoFocus
      disableRestoreFocus
      PaperProps={{
        style: {
          borderRadius: "16px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          maxHeight: "92vh",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        },
      }}
    >
      {/* Sticky Branded Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #0284c7 0%, #0369a1 100%)",
          color: "#ffffff",
          padding: "16px 22px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <div className="d-flex align-items-center gap-3">
          <div
            style={{
              width: "42px",
              height: "42px",
              borderRadius: "10px",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(4px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ffffff",
              fontSize: "20px",
            }}
          >
            <i className="fa-solid fa-heart-pulse" />
          </div>
          <div>
            <h5 className="mb-0 text-white fw-bold" style={{ fontSize: "17px", letterSpacing: "-0.01em" }}>
              {editCase ? "Edit Physiotherapy Case" : "Refer Patient to Physiotherapy"}
            </h5>
            <small style={{ color: "rgba(255,255,255,0.85)", fontSize: "12.5px" }}>
              {loadingEditCase
                ? "Loading saved case details..."
                : editCase
                ? "Update clinical rehabilitation parameters, prescription & diagnosis"
                : "Create new clinical rehabilitation referral & set baseline goals"}
            </small>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          style={{
            background: "rgba(255,255,255,0.15)",
            border: "none",
            borderRadius: "50%",
            width: "34px",
            height: "34px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#ffffff",
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
          title="Close"
        >
          <i className="fa-solid fa-xmark fs-6" />
        </button>
      </div>

      {/* Scrollable Form Body */}
      <DialogContent
        ref={contentRef}
        style={{
          overflowY: "auto",
          padding: "20px 24px",
          backgroundColor: "#f8fafc",
        }}
      >
        <form id="referral-form" onSubmit={handleSubmit}>
          {/* Card 1: Patient Selection */}
          <div
            className="card mb-3 border-0 shadow-sm"
            style={{ borderRadius: "12px", background: "#ffffff" }}
          >
            <div className="card-body p-3">
              <div className="d-flex align-items-center gap-2 mb-2">
                <span
                  style={{
                    backgroundColor: "#e0f2fe",
                    color: "#0284c7",
                    fontWeight: "700",
                    fontSize: "12px",
                    padding: "3px 8px",
                    borderRadius: "6px",
                  }}
                >
                  STEP 1
                </span>
                <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                  Patient Information <span className="text-danger">*</span>
                </span>
              </div>

              {prefilledPatient ? (
                <div className="d-flex align-items-center gap-3 p-2 border rounded bg-light">
                  <div
                    style={{
                      width: "38px",
                      height: "38px",
                      borderRadius: "50%",
                      backgroundColor: "#0284c7",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "700",
                    }}
                  >
                    {(prefilledPatient.name || prefilledPatient.patient_name || "P")[0].toUpperCase()}
                  </div>
                  <div>
                    <h6 className="mb-0 fw-bold text-dark">
                      {prefilledPatient.name || prefilledPatient.patient_name}
                    </h6>
                    <small className="text-muted">
                      ID: {prefilledPatient.patientId || prefilledPatient.patient_id} | Country: {prefilledPatient.country || "N/A"}
                    </small>
                  </div>
                </div>
              ) : (
                <div ref={patientContainerRef} style={{ position: "relative" }}>
                  {/* Search Input Control */}
                  <div
                    style={{
                      height: "42px",
                      display: "flex",
                      alignItems: "center",
                      padding: "0 12px",
                      background: "#ffffff",
                      border: `1px solid ${patientDropdownOpen ? "#0284c7" : "#cbd5e1"}`,
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      boxShadow: patientDropdownOpen ? "0 0 0 3px rgba(2, 132, 199, 0.15)" : "none",
                    }}
                    onClick={() => {
                      setPatientDropdownOpen(true);
                      patientInputRef.current?.focus();
                    }}
                  >
                    <i className="fa fa-search" style={{ color: "#94a3b8", fontSize: "13px", marginRight: "8px" }} />
                    <input
                      ref={patientInputRef}
                      type="text"
                      style={{
                        flex: 1,
                        border: "none",
                        outline: "none",
                        background: "transparent",
                        fontSize: "13.5px",
                        color: "#0f172a",
                        padding: 0,
                        cursor: "text",
                      }}
                      placeholder={
                        loadingPatients
                          ? "Loading patients..."
                          : `Search by Name, Patient ID, Phone... (${patientsList.length} total)`
                      }
                      value={
                        patientDropdownOpen
                          ? patientSearchTerm
                          : selectedPatient
                          ? (selectedPatient.name || selectedPatient.patient_name || "")
                          : ""
                      }
                      onFocus={() => {
                        setPatientDropdownOpen(true);
                        setPatientSearchTerm("");
                      }}
                      onChange={(e) => {
                        setPatientSearchTerm(e.target.value);
                        setPatientDropdownOpen(true);
                      }}
                      autoComplete="off"
                      disabled={loadingPatients}
                    />
                    {patientSearchTerm && (
                      <button
                        type="button"
                        style={{
                          border: "none",
                          background: "transparent",
                          color: "#94a3b8",
                          fontSize: "18px",
                          lineHeight: 1,
                          padding: "0 6px",
                          cursor: "pointer",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPatientSearchTerm("");
                          patientInputRef.current?.focus();
                        }}
                        tabIndex={-1}
                      >
                        ×
                      </button>
                    )}
                    <i
                      className={`fa fa-chevron-${patientDropdownOpen ? "up" : "down"}`}
                      style={{ color: "#64748b", fontSize: "11px", marginLeft: "8px" }}
                    />
                  </div>

                  {/* Dropdown List */}
                  {patientDropdownOpen && (
                    <div
                      style={{
                        position: "absolute",
                        top: "calc(100% + 4px)",
                        left: 0,
                        right: 0,
                        background: "#ffffff",
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                        boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.05)",
                        zIndex: 1050,
                        maxHeight: "300px",
                        overflowY: "auto",
                      }}
                    >
                      {filteredPatients.length === 0 ? (
                        <div style={{ padding: "12px 16px", fontSize: "13px", color: "#94a3b8", textAlign: "center" }}>
                          No patient found matching "{patientSearchTerm}"
                        </div>
                      ) : (
                        <div style={{ padding: "4px 0" }}>
                          {filteredPatients.slice(0, 250).map((p) => {
                            const pKey = p._id || p.patientId || p.patient_id || "";
                            const selKey = selectedPatient?._id || selectedPatient?.patientId || selectedPatient?.patient_id || "";
                            const isSelected = !!(pKey && selKey && pKey === selKey);
                            return (
                              <div
                                key={p._id}
                                onMouseDown={() => {
                                  setSelectedPatient(p);
                                  setPatientSearchTerm("");
                                  setPatientDropdownOpen(false);
                                }}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  padding: "8px 14px",
                                  cursor: "pointer",
                                  borderBottom: "1px solid #f1f5f9",
                                  backgroundColor: isSelected ? "#e0f2fe" : "transparent",
                                  transition: "background-color 0.15s ease",
                                }}
                                onMouseEnter={(e) => {
                                  if (!isSelected) e.currentTarget.style.backgroundColor = "#f8fafc";
                                }}
                                onMouseLeave={(e) => {
                                  if (!isSelected) e.currentTarget.style.backgroundColor = "transparent";
                                }}
                              >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <span
                                      className="fw-semibold"
                                      style={{ fontSize: "13.5px", color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                                    >
                                      {p.name || p.patient_name || "N/A"}
                                    </span>
                                    {(p.patientId || p.patient_id) && (
                                      <span
                                        style={{
                                          backgroundColor: "#e0f2fe",
                                          color: "#0369a1",
                                          border: "1px solid #bae6fd",
                                          fontSize: "11px",
                                          padding: "2px 6px",
                                          borderRadius: "4px",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {p.patientId || p.patient_id}
                                      </span>
                                    )}
                                    {p.source && (
                                      <span
                                        style={{
                                          backgroundColor: "#f8fafc",
                                          color: "#64748b",
                                          border: "1px solid #e2e8f0",
                                          fontSize: "10px",
                                          padding: "1px 5px",
                                          borderRadius: "4px",
                                        }}
                                      >
                                        {p.source}
                                      </span>
                                    )}
                                  </div>
                                  <div className="d-flex align-items-center gap-3 mt-1" style={{ fontSize: "11.5px", color: "#64748b" }}>
                                    {(p.phone || p.contact_number) && (p.phone || p.contact_number) !== "-" && (
                                      <span>
                                        <i className="fa fa-phone me-1" style={{ fontSize: "10px" }} />
                                        {p.phone || p.contact_number}
                                      </span>
                                    )}
                                    {p.country && (
                                      <span>
                                        <i className="fa fa-globe me-1" style={{ fontSize: "10px" }} />
                                        {p.country}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {isSelected && <i className="fa fa-check text-primary ms-2" />}
                              </div>
                            );
                          })}
                          {filteredPatients.length > 250 && (
                            <div style={{ textAlign: "center", padding: "8px", fontSize: "12px", color: "#64748b", background: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
                              Showing first 250 of {filteredPatients.length} matching patients. Type to refine search.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Card 2: Clinical Details */}
          <div
            className="card mb-3 border-0 shadow-sm"
            style={{ borderRadius: "12px", background: "#ffffff" }}
          >
            <div className="card-body p-3">
              <div className="d-flex align-items-center gap-2 mb-3">
                <span
                  style={{
                    backgroundColor: "#fef3c7",
                    color: "#d97706",
                    fontWeight: "700",
                    fontSize: "12px",
                    padding: "3px 8px",
                    borderRadius: "6px",
                  }}
                >
                  STEP 2
                </span>
                <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                  Clinical Referral Details
                </span>
              </div>

              <div className="row g-3">
                <div className="col-md-7">
                  <label className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>
                    Clinical Diagnosis <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="clinicalDiagnosis"
                    className="form-control"
                    placeholder="e.g. Post-operative Right Knee Arthroscopy Rehab"
                    value={formData.clinicalDiagnosis}
                    onChange={handleChange}
                    required
                    style={{ fontSize: "13.5px" }}
                  />
                </div>

                <div className="col-md-5">
                  <label className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>
                    Rehab Category
                  </label>
                  <select
                    name="category"
                    className="form-select"
                    value={formData.category}
                    onChange={handleChange}
                    style={{ fontSize: "13.5px" }}
                  >
                    {PHYSIO_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>
                    Affected Body Part / Area
                  </label>
                  <input
                    type="text"
                    name="affectedBodyPart"
                    className="form-control"
                    placeholder="e.g. Right Knee, Lumbar Spine, Left Shoulder"
                    value={formData.affectedBodyPart}
                    onChange={handleChange}
                    style={{ fontSize: "13.5px" }}
                  />
                </div>

                <div className="col-md-6">
                  <label className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>
                    Referral Priority
                  </label>
                  <select
                    name="priority"
                    className="form-select"
                    value={formData.priority}
                    onChange={handleChange}
                    style={{ fontSize: "13.5px" }}
                  >
                    {PHYSIO_PRIORITIES.map((pri) => (
                      <option key={pri} value={pri}>
                        {pri}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>
                    Case Status
                  </label>
                  <select
                    name="status"
                    className="form-select"
                    value={formData.status}
                    onChange={handleChange}
                    style={{ fontSize: "13.5px", fontWeight: 600 }}
                  >
                    {PHYSIO_STATUSES.map((st) => (
                      <option key={st} value={st}>
                        {st}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12">
                  <label className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>
                    Referral Reason / Chief Complaint
                  </label>
                  <textarea
                    name="referralReason"
                    className="form-control"
                    rows="2"
                    placeholder="Clinical reason for physiotherapy referral, functional limitations, surgery details..."
                    value={formData.referralReason}
                    onChange={handleChange}
                    style={{ fontSize: "13.5px" }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Card 3: Prescription & Goals */}
          <div
            className="card mb-3 border-0 shadow-sm"
            style={{ borderRadius: "12px", background: "#ffffff" }}
          >
            <div className="card-body p-3">
              <div className="d-flex align-items-center gap-2 mb-3">
                <span
                  style={{
                    backgroundColor: "#ecfdf5",
                    color: "#059669",
                    fontWeight: "700",
                    fontSize: "12px",
                    padding: "3px 8px",
                    borderRadius: "6px",
                  }}
                >
                  STEP 3
                </span>
                <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                  Prescription & Treatment Goals
                </span>
              </div>

              <div className="row g-3">
                <div className="col-md-3">
                  <label className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>
                    Prescribed Sessions
                  </label>
                  <input
                    type="number"
                    name="prescribedSessions"
                    className="form-control"
                    min="1"
                    max="100"
                    value={formData.prescribedSessions}
                    onChange={handleChange}
                    style={{ fontSize: "13.5px" }}
                  />
                </div>

                <div className="col-md-3">
                  <label className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>
                    Session Frequency
                  </label>
                  <input
                    type="text"
                    name="sessionFrequency"
                    className="form-control"
                    placeholder="e.g. 3 times a week, Daily"
                    value={formData.sessionFrequency}
                    onChange={handleChange}
                    style={{ fontSize: "13.5px" }}
                  />
                </div>

                <div className="col-md-3">
                  <label className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>
                    Target Start Date
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    className="form-control"
                    value={formData.startDate}
                    onChange={handleChange}
                    style={{ fontSize: "13.5px" }}
                  />
                </div>

                <div className="col-md-3">
                  <label className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>
                    Expected End Date
                  </label>
                  <input
                    type="date"
                    name="expectedEndDate"
                    className="form-control"
                    value={formData.expectedEndDate}
                    onChange={handleChange}
                    style={{ fontSize: "13.5px" }}
                  />
                </div>

                <div className="col-md-6">
                  <label className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>
                    Treatment Goals
                  </label>
                  <input
                    type="text"
                    name="treatmentGoals"
                    className="form-control"
                    placeholder="e.g. Achieve 120 deg flexion, independent gait"
                    value={formData.treatmentGoals}
                    onChange={handleChange}
                    style={{ fontSize: "13.5px" }}
                  />
                </div>

                <div className="col-md-6">
                  <label className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>
                    Special Precautions / Instructions
                  </label>
                  <input
                    type="text"
                    name="specialInstructions"
                    className="form-control"
                    placeholder="e.g. Avoid high-impact weight bearing for 2 weeks"
                    value={formData.specialInstructions}
                    onChange={handleChange}
                    style={{ fontSize: "13.5px" }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Card 4: Baseline Assessment */}
          <div
            className="card mb-3 border-0 shadow-sm"
            style={{ borderRadius: "12px", background: "#ffffff" }}
          >
            <div className="card-body p-3">
              <div className="d-flex align-items-center gap-2 mb-3">
                <span
                  style={{
                    backgroundColor: "#f3e8ff",
                    color: "#7c3aed",
                    fontWeight: "700",
                    fontSize: "12px",
                    padding: "3px 8px",
                    borderRadius: "6px",
                  }}
                >
                  STEP 4
                </span>
                <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                  Initial Baseline Assessment
                </span>
              </div>

              <div className="row g-3">
                <div className="col-md-5">
                  <label className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>
                    Mobility Status
                  </label>
                  <select
                    name="mobilityScore"
                    className="form-select"
                    value={formData.mobilityScore}
                    onChange={handleChange}
                    style={{ fontSize: "13.5px" }}
                  >
                    <option value="Independent">Independent</option>
                    <option value="Assisted">Assisted</option>
                    <option value="Assisted with Cane/Crutch">Assisted with Cane/Crutch</option>
                    <option value="Assisted with Walker">Assisted with Walker</option>
                    <option value="Wheelchair Bound">Wheelchair Bound</option>
                    <option value="Bedridden">Bedridden</option>
                  </select>
                </div>

                <div className="col-md-7">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <label className="fw-semibold text-dark mb-0" style={{ fontSize: "13px" }}>
                      Baseline Pain Score (VAS 0 - 10)
                    </label>
                    <span
                      className="badge fw-bold"
                      style={{
                        backgroundColor: painBadge.bg,
                        color: painBadge.text,
                        fontSize: "12px",
                        padding: "4px 10px",
                        borderRadius: "12px",
                      }}
                    >
                      {painBadge.label}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    step="1"
                    name="baselinePainScore"
                    className="form-range mt-2"
                    value={formData.baselinePainScore}
                    onChange={handleChange}
                  />
                  <div className="d-flex justify-content-between text-muted" style={{ fontSize: "11px" }}>
                    <span>0: No Pain</span>
                    <span>5: Moderate</span>
                    <span>10: Severe</span>
                  </div>
                </div>

                <div className="col-12">
                  <label className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>
                    Range of Motion (ROM) / Functional Notes
                  </label>
                  <input
                    type="text"
                    name="rangeOfMotionNotes"
                    className="form-control"
                    placeholder="e.g. Active flexion limited to 60 deg, extension lag 10 deg"
                    value={formData.rangeOfMotionNotes}
                    onChange={handleChange}
                    style={{ fontSize: "13.5px" }}
                  />
                </div>

                <div className="col-md-6">
                  <label className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>
                    Posture Evaluation
                  </label>
                  <input
                    type="text"
                    name="postureEvaluation"
                    className="form-control"
                    placeholder="e.g. Slight antalgic gait, normal spinal alignment"
                    value={formData.postureEvaluation}
                    onChange={handleChange}
                    style={{ fontSize: "13.5px" }}
                  />
                </div>

                <div className="col-md-6">
                  <label className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>
                    Initial Assessment Remarks
                  </label>
                  <input
                    type="text"
                    name="assessmentNotes"
                    className="form-control"
                    placeholder="e.g. Patient is motivated, cooperative"
                    value={formData.assessmentNotes}
                    onChange={handleChange}
                    style={{ fontSize: "13.5px" }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Card 5: Therapy modalities (moved from session log) */}
          <div
            className="card border-0 shadow-sm"
            style={{ borderRadius: "12px", background: "#ffffff" }}
          >
            <div className="card-body p-3">
              <div className="d-flex align-items-center gap-2 mb-2">
                <span
                  style={{
                    backgroundColor: "#e0e7ff",
                    color: "#4f46e5",
                    fontWeight: "700",
                    fontSize: "12px",
                    padding: "3px 8px",
                    borderRadius: "6px",
                  }}
                >
                  STEP 5
                </span>
                <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                  Electrotherapy & Modalities
                </span>
              </div>
              <p className="text-muted small mb-2">
                Click to select the therapy modalities prescribed for this referral:
              </p>

              <div className="d-flex flex-wrap gap-2">
                {PHYSIO_MODALITIES.map((mod) => {
                  const isSelected = isModalitySelected(mod, selectedModalities);
                  return (
                    <button
                      type="button"
                      key={mod}
                      onClick={() => handleToggleModality(mod)}
                      className={`btn btn-sm ${
                        isSelected
                          ? "btn-primary shadow-sm"
                          : "btn-outline-secondary bg-white text-dark"
                      }`}
                      style={{
                        borderRadius: "20px",
                        fontSize: "12.5px",
                        padding: "5px 14px",
                      }}
                    >
                      {isSelected ? (
                        <i className="fa fa-check me-1" />
                      ) : (
                        <i className="fa fa-plus me-1 text-muted" />
                      )}
                      {mod}
                    </button>
                  );
                })}
                {/* Show any saved modalities that are not in the predefined list */}
                {selectedModalities
                  .filter(
                    (m) =>
                      !PHYSIO_MODALITIES.some(
                        (opt) =>
                          normalizeModalityLabel(opt).toLowerCase() ===
                          normalizeModalityLabel(m).toLowerCase()
                      )
                  )
                  .map((mod) => (
                    <button
                      type="button"
                      key={`extra-${mod}`}
                      onClick={() => handleToggleModality(mod)}
                      className="btn btn-sm btn-primary shadow-sm"
                      style={{
                        borderRadius: "20px",
                        fontSize: "12.5px",
                        padding: "5px 14px",
                      }}
                    >
                      <i className="fa fa-check me-1" />
                      {mod}
                    </button>
                  ))}
              </div>
              {selectedModalities.length > 0 && (
                <div className="mt-2 small text-muted">
                  Selected: {selectedModalities.join(", ")}
                </div>
              )}
            </div>
          </div>
        </form>
      </DialogContent>

      {/* Sticky Action Footer */}
      <div
        style={{
          backgroundColor: "#ffffff",
          padding: "14px 24px",
          borderTop: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "12px",
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          className="btn btn-light px-4 border fw-semibold"
          style={{ borderRadius: "8px", fontSize: "13.5px" }}
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          form="referral-form"
          className="btn btn-primary px-4 fw-bold d-flex align-items-center gap-2 shadow-sm"
          style={{
            borderRadius: "8px",
            backgroundColor: "#0284c7",
            borderColor: "#0284c7",
            fontSize: "13.5px",
          }}
          disabled={submitting}
        >
          {submitting ? (
            <>
              <i className="fa fa-spinner fa-spin" /> {editCase ? "Updating Case..." : "Saving Referral..."}
            </>
          ) : (
            <>
              <i className="fa fa-check-circle" /> {editCase ? "Update Case" : "Create Referral"}
            </>
          )}
        </button>
      </div>
    </Dialog>
  );
}
